<template>
  <el-dialog
    :model-value="modelValue"
    title="更新渠道码"
    width="600px"
    align-center
    class="channel-code-dialog"
    :close-on-click-modal="false"
    @update:model-value="handleDialogVisibleChange"
    @closed="handleDialogClosed"
  >
    <el-form
      ref="channelCodeForm"
      :model="channelCodeForm"
      :rules="channelCodeFormRules"
      class="channel-code-form"
    >
      <div class="form-item">
        <label class="form-label required">渠道码</label>
        <div class="form-input-wrapper">
          <el-form-item
            prop="channelCode"
            class="form-item-inline"
          >
            <el-input
              v-model="channelCodeForm.channelCode"
              placeholder="请输入"
              class="form-input"
              maxlength="50"
              show-word-limit
            />
          </el-form-item>
        </div>
      </div>
      <div class="form-item">
        <label class="form-label required">失效时间<el-tooltip
          content="请设置渠道码的失效时间"
          placement="top"
        ><span class="tip-icon">?</span></el-tooltip></label>
        <div class="form-input-wrapper">
          <div class="date-picker-wrapper">
            <el-form-item
              prop="channelCodeExpireTime"
              class="form-item-inline"
            >
              <el-date-picker
                v-model="channelCodeForm.channelCodeExpireTime"
                type="datetime"
                placeholder="请选择到期时间"
                :class="['form-input', { 'is-expired': isExpired }]"
                style="width: 100%"
                :disabled-date="disabledDate"
                popper-class="channel-code-date-popper"
              />
            </el-form-item>
            <div class="quick-select-buttons">
              <el-button
                v-for="option in quickSelectOptions"
                :key="option.label"
                size="small"
                :type="option.type"
                @click="handleQuickSelect(option.value)"
              >
                {{ option.label }}
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </el-form>
    <template #footer>
      <div class="dialog-footer">
        <el-button
          type="primary"
          :loading="submitLoading"
          @click="handleSubmit"
        >
          确 定
        </el-button>
        <el-button @click="handleClose">
          取 消
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { updateChannelCode } from '@/api/workbench'
import { getCurrentEnterprise } from '@/api/enterprise'
import { formatDateTime } from '@/utils/dateUtils'

export default {
  name: 'ChannelCodeModify',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'success'],
  data() {
    return {
      submitLoading: false,
      channelCodeForm: {
        channelCode: '',
        channelCodeExpireTime: ''
      },
      channelCodeFormRules: {
        channelCode: [
          { required: true, message: '请输入渠道码', trigger: 'blur' },
          { min: 1, max: 50, message: '渠道码长度在 1 到 50 个字符', trigger: 'blur' }
        ],
        channelCodeExpireTime: [
          { required: true, message: '请选择失效时间', trigger: 'change' }
        ]
      },
      quickSelectOptions: [
        { label: '1天', value: 1, type: '' },
        { label: '3天', value: 3, type: '' },
        { label: '7天', value: 7, type: '' },
        { label: '15天', value: 15, type: '' },
        { label: '30天', value: 30, type: '' }
      ]
    }
  },
  computed: {
    /**
     * 判断失效时间是否已过期（当前时间大于输入框的时间）
     */
    isExpired() {
      if (!this.channelCodeForm.channelCodeExpireTime) {
        return false
      }
      const expireTime = new Date(this.channelCodeForm.channelCodeExpireTime).getTime()
      const currentTime = Date.now()
      return currentTime > expireTime
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadEnterpriseData()
        }
      }
    }
  },
  methods: {
    /**
     * 禁用当前时间之前的日期时间
     */
    disabledDate(time) {
      return time.getTime() < Date.now()
    },
    /**
     * 快速选择失效时间
     */
    handleQuickSelect(value) {
      // 计算未来N天的日期时间
      const now = new Date()
      const futureDate = new Date(now.getTime() + value * 24 * 60 * 60 * 1000)
      // 设置为当天的23:59:59
      futureDate.setHours(23, 59, 59, 999)
      this.channelCodeForm.channelCodeExpireTime = futureDate
      // 清除验证状态，重新验证
      this.$nextTick(() => {
        if (this.$refs.channelCodeForm) {
          this.$refs.channelCodeForm.validateField('channelCodeExpireTime')
        }
      })
    },
    /**
     * 加载渠道码数据
     */
    async loadEnterpriseData() {
      // 等待DOM更新
      await this.$nextTick()
      // 获取当前企业的渠道码信息
      try {
        const response = await getCurrentEnterprise()
        if (response && response.data) {
          const channelCodeInfo = response.data
          this.channelCodeForm.channelCode = channelCodeInfo.channelCode || ''
          // 处理过期时间：如果是时间戳，转换为Date对象；如果是字符串，直接使用
          if (channelCodeInfo.channelCodeExpireTime) {
            if (typeof channelCodeInfo.channelCodeExpireTime === 'string') {
              this.channelCodeForm.channelCodeExpireTime = new Date(channelCodeInfo.channelCodeExpireTime)
            } else if (typeof channelCodeInfo.channelCodeExpireTime === 'number') {
              // 如果是时间戳，转换为Date对象
              this.channelCodeForm.channelCodeExpireTime = new Date(channelCodeInfo.channelCodeExpireTime)
            } else {
              this.channelCodeForm.channelCodeExpireTime = channelCodeInfo.channelCodeExpireTime
            }
          }
        }
      } catch (error) {
        console.error('获取渠道码信息失败:', error)
        // 即使获取失败，弹窗也应该保持打开状态
        this.$message.warning(error.response?.data?.msg || error.message || '获取渠道码信息失败，您可以手动输入渠道码')
      }
    },
    /**
     * 处理对话框显示状态变化
     */
    handleDialogVisibleChange(value) {
      this.$emit('update:modelValue', value)
    },
    /**
     * 关闭对话框
     */
    handleClose() {
      this.$emit('update:modelValue', false)
    },
    /**
     * 弹窗完全关闭后的回调
     */
    handleDialogClosed() {
      // 重置表单
      this.$refs.channelCodeForm && this.$refs.channelCodeForm.resetFields()
      this.channelCodeForm = {
        channelCode: '',
        channelCodeExpireTime: ''
      }
    },
    /**
     * 提交渠道码修改
     */
    handleSubmit() {
      this.$refs.channelCodeForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true
          try {
            const requestData = {
              channelCode: this.channelCodeForm.channelCode
            }
            // 格式化失效时间
            requestData.channelCodeExpireTime = formatDateTime(this.channelCodeForm.channelCodeExpireTime)
            
            const response = await updateChannelCode(requestData)
            if (response && response.code === 200) {
              this.$message.success('渠道码修改成功')
              this.handleClose()
              this.$emit('success')
            } else {
              this.$message.error(response.msg || '渠道码修改失败')
            }
          } catch (error) {
            console.error('修改渠道码失败:', error)
            this.$message.error('修改渠道码失败，请稍后重试')
          } finally {
            this.submitLoading = false
          }
        } else {
          return false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  :deep(.el-button) {
    min-width: 88px;
    margin-left: 0;
  }
}

.channel-code-form {
  min-width: 0;

  .form-item {
    margin-bottom: 30px;
    display: flex;
    align-items: flex-start;
    gap: 20px;

    .form-label {
      flex-shrink: 0;
      width: 100px;
      font-size: 14px;
      color: #333333;
      font-weight: 600;
      line-height: 44px;
      display: flex;
      align-items: center;

      &.required::before {
        content: '*';
        color: #f56c6c;
        margin-right: 4px;
      }

      .tip-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        margin-left: 4px;
        border-radius: 50%;
        background-color: #e4e7ed;
        color: #909399;
        font-size: 12px;
        font-weight: normal;
        cursor: help;
        line-height: 1;
        transition: all 0.3s;

        &:hover {
          background-color: #409eff;
          color: #ffffff;
        }
      }
    }

    .form-input-wrapper {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: flex-start;
      gap: 15px;

      .date-picker-wrapper {
        flex: 1;
        min-width: 0;
        max-width: 500px;
        display: flex;
        flex-direction: column;
        gap: 18px;
      }

      .form-item-inline {
        flex: 1;
        min-width: 0;
        max-width: 100%;
        margin-bottom: 0;
        position: relative;

        :deep(.el-form-item__error) {
          position: absolute;
          top: 100%;
          left: 0;
          padding-top: 4px;
          max-width: 100%;
          white-space: normal;
          overflow-wrap: anywhere;
        }
      }

      .quick-select-buttons {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;

        :deep(.el-button) {
          padding: 5px 12px;
          font-size: 12px;
          height: 28px;
        }
      }

      .form-input {
        flex: 1;
        min-width: 0;
        width: 100%;
        max-width: 500px;

        :deep(.el-input__wrapper) {
          border-radius: 4px;
          border: 1px solid #dcdfe6;
          background-color: #ffffff;
          box-shadow: none;
          min-height: 44px;
          padding: 0 12px;

          &:hover {
            border-color: #c0c4cc;
          }

          &.is-focus {
            border-color: #0d63f3;
            box-shadow: 0 0 0 1px #0d63f3 inset;
          }
        }

        :deep(.el-input__inner) {
          color: #606266;
          font-size: 14px;
          height: 44px;
          line-height: 44px;

          &::placeholder {
            color: #c0c4cc;
          }
        }
      }

      :deep(.el-date-editor) {
        width: 100%;
        max-width: 500px;
        height: auto;

        .el-input__wrapper {
          border-radius: 4px;
          border: 1px solid #dcdfe6;
          background-color: #ffffff;
          box-shadow: none;
          min-height: 44px;
          height: 44px;
          padding: 0 12px;

          &:hover {
            border-color: #c0c4cc;
          }

          &.is-focus {
            border-color: #0d63f3;
            box-shadow: 0 0 0 1px #0d63f3 inset;
          }
        }

        .el-input__inner {
          color: #606266;
          font-size: 14px;
          height: 44px;
          line-height: 44px;

          &::placeholder {
            color: #c0c4cc;
          }
        }
      }

      // 过期状态样式
      .form-input.is-expired {
        :deep(.el-input__wrapper) {
          border-color: #f56c6c;
          background-color: #fef0f0;

          &:hover {
            border-color: #f56c6c;
          }

          &.is-focus {
            border-color: #f56c6c;
            box-shadow: 0 0 0 1px #f56c6c inset;
          }
        }

        :deep(.el-input__inner) {
          color: #f56c6c;
        }
      }

      :deep(.el-date-editor.is-expired) {
        .el-input__wrapper {
          border-color: #f56c6c;
          background-color: #fef0f0;

          &:hover {
            border-color: #f56c6c;
          }

          &.is-focus {
            border-color: #f56c6c;
            box-shadow: 0 0 0 1px #f56c6c inset;
          }
        }

        .el-input__inner {
          color: #f56c6c;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .channel-code-form {
    .form-item {
      flex-direction: column;
      gap: 8px;
      margin-bottom: 26px;

      .form-label {
        width: 100%;
        line-height: 22px;
      }

      .form-input-wrapper,
      .date-picker-wrapper,
      .form-input,
      :deep(.el-date-editor) {
        width: 100%;
        max-width: none;
      }

      .form-input-wrapper {
        display: block;
      }

      .date-picker-wrapper {
        gap: 16px;
      }

      .quick-select-buttons {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 8px;

        :deep(.el-button) {
          width: 100%;
          min-width: 0;
          height: 40px;
          margin-left: 0;
          padding: 0 8px;
        }
      }
    }
  }

  .dialog-footer {
    width: 100%;
    align-items: stretch;

    :deep(.el-button) {
      flex: 1;
      min-height: 40px;
    }
  }
}

@media (max-width: 480px) {
  .channel-code-form {
    .form-item {
      margin-bottom: 24px;

      .quick-select-buttons {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }
  }

  .dialog-footer {
    flex-direction: column;
    gap: 10px;

    :deep(.el-button) {
      width: 100%;
      min-height: 44px;
    }
  }
}
</style>

<style lang="scss">
.channel-code-dialog.el-dialog {
  width: min(600px, calc(100vw - 48px));
  max-height: calc(100dvh - 48px);
  margin: 24px auto;
  padding: 0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .el-dialog__header {
    flex-shrink: 0;
    margin: 0;
    padding: 20px 24px 16px;
    border-bottom: 1px solid #ebeef5;
  }

  .el-dialog__title {
    color: #303133;
    font-size: 18px;
    font-weight: 600;
  }

  .el-dialog__headerbtn {
    top: 16px;
    right: 16px;
  }

  .el-dialog__body {
    flex: 1;
    min-height: 0;
    padding: 24px 24px 8px;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .el-dialog__footer {
    flex-shrink: 0;
    padding: 16px 24px 24px;
  }
}

@media (max-width: 768px) {
  .channel-code-dialog.el-dialog {
    width: 100vw !important;
    max-width: 100vw;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    margin: 0 !important;
    border-radius: 0;

    .el-dialog__header {
      padding: 16px 44px 14px 16px;
    }

    .el-dialog__title {
      font-size: 16px;
    }

    .el-dialog__headerbtn {
      top: 14px;
      right: 10px;
      width: 40px;
      height: 40px;
    }

    .el-dialog__body {
      padding: 18px 16px 8px;
    }

    .el-dialog__footer {
      padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
      border-top: 1px solid #ebeef5;
    }
  }
}

@media (max-width: 480px) {
  .channel-code-dialog.el-dialog {
    .el-dialog__header {
      padding: 14px 40px 12px 12px;
    }

    .el-dialog__headerbtn {
      top: 10px;
      right: 6px;
    }

    .el-dialog__body {
      padding: 16px 12px 8px;
    }

    .el-dialog__footer {
      padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
    }
  }

  .channel-code-date-popper.el-picker__popper {
    max-width: calc(100vw - 16px);

    .el-date-picker {
      width: calc(100vw - 16px);
      max-width: 322px;
    }

    .el-picker-panel__content {
      width: auto;
      margin: 12px;
    }
  }
}
</style>

